import React, { useState } from 'react';
import { Link as LinkIcon, Camera, CaretDown, Check, X } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import type { SavedProperty } from '../../lib/utils';
export type WizardPath = 'quick' | 'personalized';
interface StepModePickerProps {
  tenantName?: string;
  tenantWishlist?: {
    locations: string[];
    essentials: string[];
    preferred: string[];
  };
  savedProperties: SavedProperty[];
  onPick: (path: WizardPath) => void;
  onPickSaved: (property: SavedProperty) => void;
}
export function StepModePicker({
  tenantName,
  tenantWishlist,
  savedProperties,
  onPick,
  onPickSaved
}: StepModePickerProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  return <div className="animate-in fade-in slide-in-from-bottom-2 duration-300" data-id="element-2608">
      {tenantName && <p className="text-sm text-light-3 mb-3" data-id="element-2609">
          Trimite ofertă către{' '}
          <span className="text-light-1 font-medium" data-id="element-2610">{tenantName}</span>
          {tenantWishlist && <>
              {' · '}
              <button type="button" onClick={() => setWishlistOpen(true)} className="text-lime-1 hover:text-lime-2 underline underline-offset-2" data-id="element-2611">
                vezi ce caută
              </button>
            </>}
        </p>}

      <h2 className="text-2xl sm:text-3xl font-bold text-light-1 mb-2" data-id="element-2612">
        Cum vrei să prezinți proprietatea?
      </h2>
      <p className="text-sm text-light-3 mb-8" data-id="element-2613">
        Alege cum vrei să construiești anunțul. Poți schimba mai târziu.
      </p>

      {savedProperties.length > 0 && <div className="mb-6 rounded-xl bg-dark-2 border border-dark-3 p-3 sm:p-4" data-id="element-2614">
          <p className="text-xs font-medium text-light-3 uppercase tracking-wider mb-2" data-id="element-2615">
            Folosește o proprietate salvată
          </p>
          <div className="relative" data-id="element-2616">
            <button type="button" onClick={() => setPickerOpen(v => !v)} className="w-full inline-flex items-center justify-between gap-2 h-10 px-3 rounded-md bg-dark-3 border border-dark-4 hover:border-light-3 text-sm text-light-1 transition-colors cursor-pointer" data-id="element-2617">
              <span className="truncate" data-id="element-2618">Alege o proprietate...</span>
              <CaretDown size={14} weight="bold" className="text-light-3" data-id="element-2619" />
            </button>
            {pickerOpen && <>
                <div className="fixed inset-0 z-10" onClick={() => setPickerOpen(false)} data-id="element-2620" />
                <ul className="absolute left-0 right-0 top-full mt-1 z-20 max-h-64 overflow-y-auto rounded-md bg-dark-2 border border-white/15 py-1" data-id="element-2621">
                  {savedProperties.map(p => <li key={p.id} data-id="element-2622">
                      <button type="button" onClick={() => {
                setPickerOpen(false);
                onPickSaved(p);
              }} className="w-full text-left px-3 py-2 text-sm text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors flex items-center gap-2" data-id="element-2623">
                        <span className="truncate flex-1" data-id="element-2624">{p.name}</span>
                        {!p.isActive && <span className="text-[10px] text-light-4 uppercase tracking-wider" data-id="element-2625">
                            inactivă
                          </span>}
                      </button>
                    </li>)}
                </ul>
              </>}
          </div>
        </div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2626">
        <ModeCard icon={<LinkIcon size={22} weight="bold" data-id="element-2628" />} title="Anunț scurt" subtitle="Ai deja un anunț online (OLX, Storia, Imobiliare). Lipești linkul, alegi facilitățile, gata." time="1–2 min" onClick={() => onPick('quick')} data-id="element-2627" />
        <ModeCard icon={<Camera size={22} weight="bold" data-id="element-2630" />} title="Anunț complet" subtitle="Construiești anunțul de la zero, cu poze și toate detaliile. Apare cu badge complet în rezultate." time="5–7 min" onClick={() => onPick('personalized')} data-id="element-2629" />
      </div>

      {/* Wishlist slide-over */}
      {wishlistOpen && tenantWishlist && <div className="fixed inset-0 z-50 flex" data-id="element-2631">
          <div className="absolute inset-0 bg-dark-1/70 backdrop-blur-sm animate-in fade-in" onClick={() => setWishlistOpen(false)} data-id="element-2632" />
          <aside className="relative ml-auto w-full sm:w-[420px] h-full bg-dark-2 border-l border-dark-4 overflow-y-auto animate-in slide-in-from-right duration-300" data-id="element-2633">
            <div className="sticky top-0 bg-dark-2 border-b border-dark-4 p-5 flex items-center justify-between" data-id="element-2634">
              <h3 className="text-base font-semibold text-light-1" data-id="element-2635">
                Ce caută {tenantName}
              </h3>
              <button onClick={() => setWishlistOpen(false)} className="p-1 text-light-3 hover:text-light-1" data-id="element-2636">
                <X size={20} weight="bold" data-id="element-2637" />
              </button>
            </div>
            <div className="p-5 space-y-6" data-id="element-2638">
              <WishlistSection title="Locații" items={tenantWishlist.locations} data-id="element-2639" />
              <WishlistSection title="Facilități esențiale" items={tenantWishlist.essentials} data-id="element-2640" />
              <WishlistSection title="Facilități preferabile" items={tenantWishlist.preferred} data-id="element-2641" />
            </div>
          </aside>
        </div>}
    </div>;
}
function ModeCard({
  icon,
  title,
  subtitle,
  time,
  onClick
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  time: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className={cn('group text-left rounded-2xl p-5 min-h-[180px] flex flex-col gap-3 transition-all cursor-pointer', 'bg-dark-2 border border-dark-4 hover:border-light-3 hover:bg-dark-3')} data-id="element-2642">
      <div className="flex items-start justify-between gap-2" data-id="element-2643">
        <div className="w-11 h-11 rounded-xl bg-dark-3 border border-dark-4 inline-flex items-center justify-center text-light-1 group-hover:bg-dark-4 transition-colors" data-id="element-2644">
          {icon}
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-dark-3 border border-dark-4 text-light-3" data-id="element-2645">
          {time}
        </span>
      </div>
      <div className="flex-1" data-id="element-2646">
        <h3 className="text-base font-semibold text-light-1 mb-1.5" data-id="element-2647">{title}</h3>
        <p className="text-xs text-light-3 leading-relaxed" data-id="element-2648">{subtitle}</p>
      </div>
    </button>;
}
function WishlistSection({
  title,
  items
}: {
  title: string;
  items: string[];
}) {
  if (items.length === 0) return null;
  return <div data-id="element-2649">
      <h4 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-2" data-id="element-2650">
        {title}
      </h4>
      <div className="flex flex-wrap gap-1.5" data-id="element-2651">
        {items.map(it => <span key={it} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-dark-3 border border-dark-4 text-xs text-light-2" data-id="element-2652">
            <Check size={10} weight="bold" className="text-lime-1" data-id="element-2653" />
            {it}
          </span>)}
      </div>
    </div>;
}